import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, inject, Input,
  NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { GoogleMapsModule, MapCircle, MapRectangle } from '@angular/google-maps';
import { Subject } from 'rxjs';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { GeofenceType, WorkshopGeofence } from '../../../models/workshop.model';
import { AccountSelectorComponent } from '../../../../../../_shared/components/account-selector/account-selector.component';
import { CountryFlagComponent } from '../../../../../../_shared/components/country-flag/country-flag.component';
import { Account } from '../../../../../../_shared/models/account.model';
import { WorkshopType } from '../../../enums/maintenance-settings.enum';
import { CurrencyOption, WorkshopDetailsResponse } from '../../../models/workshop-response.model';
import { WorkshopDetailsRequest } from '../../../models/workshop-request.model';
import { WorkshopService } from '../../../services/workshop.service';

@Component({
  selector: 'app-workshop-detail',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, NgbDropdownModule, NgbTooltipModule, GoogleMapsModule, TranslocoDirective, TranslocoPipe, AccountSelectorComponent, CountryFlagComponent],
  templateUrl: './workshop-detail.component.html',
  styleUrl: './workshop-detail.component.scss'
})
export class WorkshopDetailComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() isLoading: boolean;
  @Input() isSaving: boolean;
  @Input() isDeleting: boolean;
  @Input() workshop: WorkshopDetailsResponse | null = null;
  @Input() mode: 'edit' | 'add' = 'add';
  @Output() cancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<WorkshopDetailsRequest>();
  @Output() delete = new EventEmitter<WorkshopDetailsResponse>();

  @ViewChild('locationInput') locationInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MapCircle) mapCircleRef?: MapCircle;
  @ViewChild(MapRectangle) mapRectangleRef?: MapRectangle;

  form!: FormGroup;
  isEditing: boolean = false;
  hasChanges: boolean = false;

  private readonly transloco = inject(TranslocoService);

  workshopTypes: WorkshopType[] = [WorkshopType.InHouse, WorkshopType.External];
  workshopTypeLabels: Record<WorkshopType, string> = {
    [WorkshopType.InHouse]: this.transloco.translate('assetSettings.workshops.typeLabels.inHouse'),
    [WorkshopType.External]: this.transloco.translate('assetSettings.common.external')
  }

  selectedType: string = this.workshopTypeLabels[WorkshopType.External];
  selectedAccount: Account | null = null;

  // Labour rate currency
  currencies: CurrencyOption[] = [];
  filteredCurrencies: CurrencyOption[] = [];
  selectedCurrency: CurrencyOption | null = null;
  showCurrencyDropdown: boolean = false;
  currencySearchTerm: string = '';
  isCurrenciesLoading: boolean = false;
  private currencyManuallySelected: boolean = false;
  private pendingCurrencyId: number | null = null;
  private readonly cancelSubject = new Subject<void>();

  // Map options
  mapOptions: google.maps.MapOptions = {
    center: { lat: -26.1052, lng: 28.0561 },
    zoom: 16,
    mapTypeId: 'roadmap',
    fullscreenControl: false,
    mapTypeControl: false,
    zoomControl: true,
    streetViewControl: false,
    cameraControl: false,
    mapId: 'WORKSHOP_DETAIL_MAP'
  };

  readonly customPointMarkerOptions: google.maps.MarkerOptions = {
    clickable: false,
    icon: {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 6,
      fillColor: '#EE2737',
      fillOpacity: 1,
      strokeColor: '#fff',
      strokeWeight: 2
    }
  };

  // Geofence - Circle
  circleOptions: google.maps.CircleOptions = {
    fillColor: '#EE2737',
    fillOpacity: 0.18,
    strokeColor: '#EE2737',
    strokeWeight: 2,
    editable: true,
    draggable: false,
    radius: 50,
    center: { lat: -26.1052, lng: 28.0561 }
  };

  // Geofence - Rectangle
  rectangleBounds: google.maps.LatLngBoundsLiteral | null = null;
  rectangleOptions: google.maps.RectangleOptions = {
    fillColor: '#EE2737',
    fillOpacity: 0.18,
    strokeColor: '#EE2737',
    strokeWeight: 2,
    editable: true,
    draggable: false
  };

  // Geofence - Custom polygon
  polygonPaths: google.maps.LatLngLiteral[] = [];
  polygonOptions: google.maps.PolygonOptions = {
    fillColor: '#EE2737',
    fillOpacity: 0.18,
    strokeColor: '#EE2737',
    strokeWeight: 2,
    editable: true,
    draggable: false
  };
  customPolylineOptions: google.maps.PolylineOptions = {
    strokeColor: '#EE2737',
    strokeOpacity: 1,
    strokeWeight: 2,
    clickable: false
  };
  customPoints: google.maps.LatLngLiteral[] = [];
  isDrawingCustom: boolean = false;

  // Geofence shape state
  geofenceSizeMeters: number = 50;
  activeGeofenceType: GeofenceType = 'circle';
  showCircle: boolean = false;
  showRectangle: boolean = false;
  showPolygon: boolean = false;

  // Cursor-following line during custom drawing
  cursorPosition: google.maps.LatLngLiteral | null = null;
  private mouseMoveListener: google.maps.MapsEventListener | null = null;
  readonly cursorPolylineOptions: google.maps.PolylineOptions = {
    strokeColor: '#EE2737',
    strokeOpacity: 0,
    strokeWeight: 2,
    clickable: false,
    icons: [{
      icon: {
        path: 'M 0,-1 0,1',
        strokeOpacity: 1,
        strokeWeight: 2,
        scale: 3
      },
      offset: '0',
      repeat: '12px'
    }]
  };

  // Snap threshold constants
  private readonly SNAP_BASE_DISTANCE_METERS = 50;
  private readonly SNAP_REFERENCE_ZOOM = 16;

  // Map layers
  currentMapType: google.maps.MapTypeId = 'roadmap' as google.maps.MapTypeId;
  layerPreviewUrl: string = '';

  private map: google.maps.Map | null = null;
  private idleListener: google.maps.MapsEventListener | null = null;

  // Places autocomplete
  private autocomplete: google.maps.places.Autocomplete | null = null;

  private fb: FormBuilder = inject(FormBuilder);
  private ngZone: NgZone = inject(NgZone);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly workshopService: WorkshopService = inject(WorkshopService);

  ngOnInit(): void {
    this.form = this.fb.group({
      account: ['', Validators.required],
      type: ['', Validators.required],
      name: ['', Validators.required],
      location: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', Validators.required],
      labourRate: [null]
    });

    this.form.valueChanges.subscribe(() => {
      this.hasChanges = this.checkForChanges();
    });

    this.populateForm();
    void this.loadCurrenciesAsync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workshop'] && !changes['workshop'].firstChange) {
      this.populateForm();
    }
  }

  async ngAfterViewInit(): Promise<void> {
    await this.initAutocomplete();
  }

  ngOnDestroy(): void {
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
    }
    if (this.idleListener) {
      this.idleListener.remove();
      this.idleListener = null;
    }
    this.stopCursorTracking();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  @HostListener('document:click')
  closeCurrencyDropdown(): void {
    this.showCurrencyDropdown = false;
  }

  get isInHouse(): boolean {
    return this.form?.get('type')?.value === WorkshopType.InHouse;
  }

  get geofenceSliderPercent(): number {
    const min = 50;
    const max = 1000;
    return ((this.geofenceSizeMeters - min) / (max - min)) * 100;
  }

  // --- Geofence drawing ---
  onShapeSelect(type: GeofenceType): void {
    this.activeGeofenceType = type;
    this.clearAllGeofences();

    if (type === 'custom') {
      this.isDrawingCustom = true;
      this.customPoints = [];
      this.startCursorTracking();
      this.hasChanges = this.checkForChanges();
      return;
    }

    this.isDrawingCustom = false;
    const center = this.mapOptions.center as google.maps.LatLngLiteral;
    if (center) {
      this.createGeofence(type, center);
    }
    this.hasChanges = this.checkForChanges();
  }

  createGeofence(type: GeofenceType, center: { lat: number; lng: number }): void {
    this.clearAllGeofences();
    this.activeGeofenceType = type;

    if (type === 'circle') {
      this.circleOptions = {
        ...this.circleOptions,
        center,
        radius: this.geofenceSizeMeters,
        editable: true
      };
      this.showCircle = true;
    } else if (type === 'rectangle') {
      const latOffset = this.geofenceSizeMeters / 111320;
      const lngOffset = this.geofenceSizeMeters / (111320 * Math.cos(center.lat * Math.PI / 180));

      this.rectangleBounds = {
        north: center.lat + latOffset,
        south: center.lat - latOffset,
        east: center.lng + lngOffset,
        west: center.lng - lngOffset
      };

      this.rectangleOptions = {
        ...this.rectangleOptions,
        editable: true
      };

      this.showRectangle = true;
    }
    this.hasChanges = this.checkForChanges();
  }

  onGeofenceSizeChange(value: string | number): void {
    const size = Number(value);
    if (!size || !this.mapOptions.center) return;

    this.geofenceSizeMeters = size;
    const center = this.mapOptions.center as google.maps.LatLngLiteral;

    if (this.activeGeofenceType === 'circle') {
      this.circleOptions = {
        ...this.circleOptions,
        radius: size,
        center
      };
      this.showCircle = true;
    }

    if (this.activeGeofenceType === 'rectangle') {
      const latOffset = size / 111320;
      const lngOffset = size / (111320 * Math.cos(center.lat * Math.PI / 180));

      this.rectangleBounds = {
        north: center.lat + latOffset,
        south: center.lat - latOffset,
        east: center.lng + lngOffset,
        west: center.lng - lngOffset
      };

      this.rectangleOptions = {
        ...this.rectangleOptions,
        editable: true
      };

      this.showRectangle = true;
    }
    this.hasChanges = this.checkForChanges();
  }

  onMapClick(event: google.maps.MapMouseEvent): void {
    if (!this.isDrawingCustom || !event.latLng) return;

    const point: google.maps.LatLngLiteral = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };

    if (this.isNearFirstPoint(point)) {
      this.polygonPaths = [...this.customPoints];
      this.polygonOptions = {
        ...this.polygonOptions,
        paths: this.polygonPaths,
        editable: true
      };
      this.showPolygon = true;
      this.isDrawingCustom = false;
      this.stopCursorTracking();
      this.hasChanges = this.checkForChanges();
      return;
    }

    this.customPoints = [...this.customPoints, point];
  }

  finishCustomDrawing(): void {
    this.isDrawingCustom = false;
    this.stopCursorTracking();
    if (this.customPoints.length >= 3) {
      this.polygonPaths = [...this.customPoints];
      this.polygonOptions = { ...this.polygonOptions, paths: this.polygonPaths };
      this.showPolygon = true;
    }
  }

  onCircleEdited(): void {
    const circle = this.mapCircleRef?.circle;
    if (!circle) return;
    const center = circle.getCenter();
    const radius = circle.getRadius();
    if (center) {
      this.circleOptions = {
        ...this.circleOptions,
        center: { lat: center.lat(), lng: center.lng() },
        radius
      };
      this.geofenceSizeMeters = Math.round(radius);
    }
    this.hasChanges = this.checkForChanges();
  }

  onRectangleEdited(): void {
    const rect = this.mapRectangleRef?.rectangle;
    if (!rect) return;
    const bounds = rect.getBounds();
    if (bounds) {
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      this.rectangleBounds = {
        north: ne.lat(),
        south: sw.lat(),
        east: ne.lng(),
        west: sw.lng()
      };
    }
    this.hasChanges = this.checkForChanges();
  }

  clearAllGeofences(): void {
    this.showCircle = false;
    this.showRectangle = false;
    this.showPolygon = false;
    this.customPoints = [];
    this.polygonPaths = [];
    this.isDrawingCustom = false;
    this.stopCursorTracking();
  }

  // --- Map layers ---
  onMapInit(map: google.maps.Map): void {
    this.map = map;
    this.updateLayerPreview();
    this.idleListener = this.map.addListener('idle', () => {
      this.ngZone.run(() => this.updateLayerPreview());
    });
  }

  setMapType(mapType: google.maps.MapTypeId): void {
    this.currentMapType = mapType;

    this.mapOptions = {
      ...this.mapOptions,
      mapTypeId: mapType
    };

    if (this.map) {
      this.map.setMapTypeId(mapType);
    }
  }

  toggleMapLayer(): void {
    const nextMapType: google.maps.MapTypeId = this.currentMapType === 'roadmap' ? 'satellite' as google.maps.MapTypeId : 'roadmap' as google.maps.MapTypeId;
    this.setMapType(nextMapType);
    this.updateLayerPreview();
  }

  // --- Form actions ---
  enableEditing(): void {
    this.isEditing = true;
    this.form.enable();
    this.hasChanges = false;
    this.syncGeofenceEditState();

    setTimeout(() => this.initAutocomplete(), 100);
  }

  onSelectAccount(account: Account): void {
    this.selectedAccount = account;
    this.form.get('account')?.setValue(account?.name || '');
    this.hasChanges = this.checkForChanges();
    void this.applyAccountDefaultCurrencyAsync(account?.id ?? null);
  }

  onTypeSelect(type: WorkshopType): void {
    this.selectedType = this.workshopTypeLabels[type];
    this.form.get('type')?.setValue(type);

    if (type === WorkshopType.External) {
      this.form.get('labourRate')?.setValue(null);
      this.selectedCurrency = null;
      this.currencyManuallySelected = false;
    }
  }

  // --- Labour rate currency ---
  toggleCurrencyDropdown(event: Event): void {
    event.stopPropagation();
    if (!this.isEditing) return;

    this.showCurrencyDropdown = !this.showCurrencyDropdown;
    this.currencySearchTerm = '';
    this.filteredCurrencies = [...this.currencies];
  }

  filterCurrencies(): void {
    const searchTerm = this.currencySearchTerm.toLowerCase().trim();
    this.filteredCurrencies = searchTerm === ''
      ? [...this.currencies]
      : this.currencies.filter(c => c.name.toLowerCase().includes(searchTerm) || c.code.toLowerCase().includes(searchTerm));
  }

  selectCurrency(currency: CurrencyOption): void {
    this.selectedCurrency = currency;
    this.currencyManuallySelected = true;
    this.showCurrencyDropdown = false;
    this.currencySearchTerm = '';
    this.filteredCurrencies = [...this.currencies];
    this.hasChanges = this.checkForChanges();
  }

  private async loadCurrenciesAsync(): Promise<void> {
    this.isCurrenciesLoading = true;
    try {
      const response = await this.workshopService.getCurrencyOptions(this.cancelSubject);
      this.currencies = response.data ?? [];
      this.filteredCurrencies = [...this.currencies];
      this.resolvePendingCurrency();
    } catch {
      this.currencies = [];
      this.filteredCurrencies = [];
    } finally {
      this.isCurrenciesLoading = false;
    }
  }

  private resolvePendingCurrency(): void {
    if (this.pendingCurrencyId === null || this.currencies.length === 0) return;

    this.selectedCurrency = this.currencies.find(c => c.id === this.pendingCurrencyId) ?? null;
    this.pendingCurrencyId = null;
  }

  private async applyAccountDefaultCurrencyAsync(accountId: number | null): Promise<void> {
    if (!accountId || this.currencyManuallySelected || this.selectedCurrency !== null || this.pendingCurrencyId !== null) return;

    try {
      const response = await this.workshopService.getAccountCurrency(accountId, this.cancelSubject);
      const currencyId = response.data?.currencyId ?? null;
      if (currencyId === null || this.currencyManuallySelected || this.selectedCurrency !== null || this.pendingCurrencyId !== null) return;

      if (this.currencies.length === 0) {
        this.pendingCurrencyId = currencyId;
        return;
      }

      this.selectedCurrency = this.currencies.find(c => c.id === currencyId) ?? null;
    } catch {
      // no default applied; the user can still pick a currency manually
    }
  }

  onSave(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    const formValue = this.form.getRawValue();
    const geofence = this.getCurrentGeofence();
    const isInHouse = formValue.type === WorkshopType.InHouse;
    const labourRate = isInHouse && formValue.labourRate !== null && formValue.labourRate !== '' ? Number(formValue.labourRate) : null;
    const workshop: WorkshopDetailsRequest = {
      id: this.workshop?.id ?? 0,
      name: formValue.name,
      location: formValue.location,
      type: formValue.type,
      accountId: this.selectedAccount?.id ?? this.workshop?.accountId ?? 0,
      account: formValue.account,
      email: formValue.email,
      phoneNumber: formValue.phoneNumber,
      labourRate,
      labourRateCurrencyId: labourRate === null ? null : this.selectedCurrency?.id ?? null,
      latitude: (this.mapOptions.center as google.maps.LatLngLiteral)?.lat,
      longitude: (this.mapOptions.center as google.maps.LatLngLiteral)?.lng,
      geofence
    };

    this.save.emit(workshop);
  }

  onCancel(): void {
    if (this.mode === 'edit') {
      this.isEditing = false;
      this.form.disable();
      this.form.markAsPristine();
      this.form.markAsUntouched();
      this.clearAllGeofences();
      if (this.workshop?.geofence) {
        this.activeGeofenceType = this.workshop.geofence.type;
        this.restoreGeofence(this.workshop.geofence);
      }
      this.syncGeofenceEditState();
      this.hasChanges = false;
      return;
    }

    this.cancel.emit();
  }

  onDelete(): void {
    if (this.workshop) {
      this.delete.emit(this.workshop);
    }
  }

  isFieldInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!(control && control.invalid && control.touched);
  }

  private updateLayerPreview(): void {
    if (!this.map) return;

    const center = this.map.getCenter();
    if (!center) return;

    const zoom = Math.min(Math.max(Math.round(this.map.getZoom() ?? 0), 0), 21);
    const lat = center.lat();
    const lng = center.lng();

    // Convert lat/lng to tile coordinates
    const scale = Math.pow(2, zoom);
    const x = ((Math.floor(((lng + 180) / 360) * scale) % scale) + scale) % scale;
    const latRad = (lat * Math.PI) / 180;
    const y = Math.max(0, Math.min(scale - 1, Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * scale)));

    // lyrs: m = roadmap, s = satellite
    const previewLyrs = this.currentMapType === 'roadmap' ? 's' : 'm';
    this.layerPreviewUrl = `https://mt1.google.com/vt/lyrs=${previewLyrs}&x=${x}&y=${y}&z=${zoom}`;
  }

  // --- Form population ---
  private populateForm(): void {
    if (!this.form) return;

    this.clearAllGeofences();

    if (this.mode === 'edit' && this.workshop) {
      this.form.patchValue({
        account: this.workshop.account,
        type: this.workshop.type,
        name: this.workshop.name,
        location: this.workshop.location,
        email: this.workshop.email,
        phoneNumber: this.workshop.phoneNumber,
        labourRate: this.workshop.labourRate
      });
      this.currencyManuallySelected = false;
      if (this.workshop.labourRateCurrencyId !== null) {
        this.pendingCurrencyId = this.workshop.labourRateCurrencyId;
        this.resolvePendingCurrency();
      } else {
        this.selectedCurrency = null;
      }
      this.selectedType = this.workshop.type === WorkshopType.InHouse ? this.workshopTypeLabels[WorkshopType.InHouse] : this.workshopTypeLabels[WorkshopType.External];
      this.form.disable();
      this.isEditing = false;
      this.hasChanges = false;
      this.form.markAsPristine();
      this.form.markAsUntouched();
      this.syncGeofenceEditState();

      if (this.workshop.latitude && this.workshop.longitude) {
        this.mapOptions = {
          ...this.mapOptions,
          center: { lat: this.workshop.latitude, lng: this.workshop.longitude }
        };
      }

      if (this.workshop.geofence) {
        this.activeGeofenceType = this.workshop.geofence.type;
        this.restoreGeofence(this.workshop.geofence);
      }
    } else {
      this.form.reset({ type: WorkshopType.External });
      this.form.enable();
      this.isEditing = true;
      this.hasChanges = false;
      this.selectedType = this.workshopTypeLabels[WorkshopType.External];
      this.syncGeofenceEditState();

      this.mapOptions = {
        ...this.mapOptions,
        center: { lat: -26.1052, lng: 28.0561 }
      };
    }
  }

  private syncGeofenceEditState(): void {
    this.circleOptions = {
      ...this.circleOptions,
      editable: this.isEditing
    };

    this.rectangleOptions = {
      ...this.rectangleOptions,
      editable: this.isEditing
    };

    this.polygonOptions = {
      ...this.polygonOptions,
      editable: this.isEditing
    };
  }

  private restoreGeofence(geofence: WorkshopGeofence): void {
    switch (geofence.type) {
      case 'circle':
        this.circleOptions = {
          ...this.circleOptions,
          center: geofence.center,
          radius: geofence.radiusMeters ?? 200
        };
        this.showCircle = true;
        break;
      case 'rectangle':
        if (geofence.coordinates?.length === 4) {
          const lats = geofence.coordinates.map(c => c.lat);
          const lngs = geofence.coordinates.map(c => c.lng);
          this.rectangleBounds = {
            north: Math.max(...lats),
            south: Math.min(...lats),
            east: Math.max(...lngs),
            west: Math.min(...lngs)
          };
          this.rectangleOptions = { ...this.rectangleOptions, bounds: this.rectangleBounds };
          this.showRectangle = true;
        }
        break;
      case 'custom':
        if (geofence.coordinates?.length) {
          this.polygonPaths = [...geofence.coordinates];
          this.polygonOptions = { ...this.polygonOptions, paths: this.polygonPaths };
          this.customPoints = [...geofence.coordinates];
          this.showPolygon = true;
        }
        break;
    }
  }

  // --- Google Places Autocomplete ---
  private async initAutocomplete(): Promise<void> {
    try {
      await Promise.all([
        google.maps.importLibrary('places'),
        google.maps.importLibrary('geometry')
      ]);
    } catch {
      console.warn('Google Places library could not be loaded');
      return;
    }

    if (!this.locationInput?.nativeElement) return;

    this.autocomplete = new google.maps.places.Autocomplete(
      this.locationInput.nativeElement,
      {
        types: ['establishment', 'geocode'],
        fields: ['formatted_address', 'geometry', 'name']
      }
    );

    this.autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = this.autocomplete!.getPlace();
        if (place.geometry?.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          this.form.get('location')?.setValue(place.formatted_address || place.name || '');
          this.form.get('location')?.markAsDirty();
          this.hasChanges = this.checkForChanges();

          this.mapOptions = { ...this.mapOptions, center: { lat, lng } };
          this.geofenceSizeMeters = 50;
          this.createGeofence('circle', { lat, lng });
        }
      });
    });
  }

  private getSnapThreshold(): number {
    const currentZoom = this.map?.getZoom() ?? this.SNAP_REFERENCE_ZOOM;
    return this.SNAP_BASE_DISTANCE_METERS * Math.pow(2, this.SNAP_REFERENCE_ZOOM - currentZoom);
  }

  private isNearFirstPoint(point: google.maps.LatLngLiteral): boolean {
    if (this.customPoints.length < 3) return false;

    const first = this.customPoints[0];
    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(point.lat, point.lng),
      new google.maps.LatLng(first.lat, first.lng)
    );

    return distance <= this.getSnapThreshold();
  }

  get cursorPolylinePath(): google.maps.LatLngLiteral[] {
    if (!this.cursorPosition || this.customPoints.length === 0) return [];
    const lastPoint = this.customPoints[this.customPoints.length - 1];
    return [lastPoint, this.cursorPosition];
  }

  private startCursorTracking(): void {
    this.stopCursorTracking();
    if (!this.map) return;

    let pendingFrame = false;
    this.ngZone.runOutsideAngular(() => {
      this.mouseMoveListener = this.map!.addListener('mousemove', (event: google.maps.MapMouseEvent) => {
        if (!event.latLng) return;
        this.cursorPosition = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        };
        if (!pendingFrame) {
          pendingFrame = true;
          requestAnimationFrame(() => {
            pendingFrame = false;
            this.cdr.detectChanges();
          });
        }
      });
    });
  }

  private stopCursorTracking(): void {
    if (this.mouseMoveListener) {
      this.mouseMoveListener.remove();
      this.mouseMoveListener = null;
    }
    this.cursorPosition = null;
  }

  private getCurrentGeofence(): WorkshopGeofence | null {
    if (this.showCircle) {
      return {
        type: 'circle',
        center: this.circleOptions.center as { lat: number; lng: number },
        radiusMeters: (this.circleOptions.radius as number) ?? 200
      };
    } else if (this.showRectangle && this.rectangleBounds) {
      return {
        type: 'rectangle',
        center: {
          lat: (this.rectangleBounds.north + this.rectangleBounds.south) / 2,
          lng: (this.rectangleBounds.east + this.rectangleBounds.west) / 2
        },
        coordinates: [
          { lat: this.rectangleBounds.north, lng: this.rectangleBounds.west },
          { lat: this.rectangleBounds.north, lng: this.rectangleBounds.east },
          { lat: this.rectangleBounds.south, lng: this.rectangleBounds.east },
          { lat: this.rectangleBounds.south, lng: this.rectangleBounds.west }
        ]
      };
    } else if (this.showPolygon && this.polygonPaths.length >= 3) {
      const centroid = this.polygonPaths.reduce(
        (acc, p) => ({
          lat: acc.lat + p.lat / this.polygonPaths.length,
          lng: acc.lng + p.lng / this.polygonPaths.length
        }),
        { lat: 0, lng: 0 }
      );
      return {
        type: 'custom',
        center: centroid,
        coordinates: [...this.polygonPaths]
      };
    }
    return null;
  }

  private hasGeofenceChanged(): boolean {
    const current = this.getCurrentGeofence();
    const original = this.workshop?.geofence ?? null;

    // Both null — no change
    if (!current && !original) return false;
    // One null, other not — changed
    if (!current || !original) return true;
    // Type differs — changed
    if (current.type !== original.type) return true;

    if (current.type === 'circle') {
      return (
        current.center.lat !== original.center.lat ||
        current.center.lng !== original.center.lng ||
        current.radiusMeters !== original.radiusMeters
      );
    }

    // Rectangle or custom — compare coordinates
    const currentCoords = current.coordinates ?? [];
    const originalCoords = original.coordinates ?? [];
    if (currentCoords.length !== originalCoords.length) return true;
    return currentCoords.some((c, i) =>
      c.lat !== originalCoords[i].lat || c.lng !== originalCoords[i].lng
    );
  }

  private checkForChanges(): boolean {
    if (this.mode === 'add') return true;
    if (!this.workshop) return false;

    const formValue = this.form.getRawValue();
    return (
      formValue.name !== this.workshop.name ||
      formValue.location !== this.workshop.location ||
      formValue.email !== this.workshop.email ||
      formValue.phoneNumber !== this.workshop.phoneNumber ||
      formValue.type !== this.workshop.type ||
      formValue.account !== this.workshop.account ||
      (this.selectedAccount?.id != null && this.selectedAccount.id !== this.workshop.accountId) ||
      (formValue.labourRate ?? null) !== (this.workshop.labourRate ?? null) ||
      (this.selectedCurrency?.id ?? null) !== (this.workshop.labourRateCurrencyId ?? null) ||
      this.hasGeofenceChanged()
    );
  }

  get isCancelDisabled(): boolean {
    if (this.mode === 'add') return false;
    return !this.isEditing;
  }

  get isSaveDisabled(): boolean {
    if (this.mode === 'add') {
      return this.form.invalid;
    }
    return !this.hasChanges || this.form.invalid || !this.isEditing;
  }
}
